<template>
    <!-- Developer harness for the report settings components (ENGS-677).
         These components are shared by every report page, so exercising them
         here -- with fixture data rather than a real company's meters and
         groups -- lets a reviewer see each one in isolation, at sizes real data
         does not always produce (an empty picker, a 100-user picker, every
         field type at once). -->
    <div class="mx-auto max-w-5xl px-4 py-8">
        <h1 class="m-0 p-0 text-2xl font-bold text-gray-900">
            Report Components
        </h1>
        <p class="mt-1 text-base text-gray-600">
            Each component below is rendered on its own. The live value is shown
            beside it so you can confirm what it emits.
        </p>

        <!-- ReportPageHeader ------------------------------------------------->
        <section class="mt-8 rounded-md border border-gray-200 bg-white p-4">
            <h2 class="m-0 mb-3 p-0 text-lg font-semibold text-gray-900">
                ReportPageHeader
            </h2>

            <report-page-header title="Excursion Report"
                                description="Twice-daily readings in tabular and graph form, with alarm details."
            >
                <template   #icon>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
                        <rect x="9" y="3" width="6" height="4" rx="2" />
                    </svg>
                </template>
            </report-page-header>
        </section>

        <!-- TablerIcon ------------------------------------------------------->
        <section class="mt-6 rounded-md border border-gray-200 bg-white p-4">
            <h2 class="m-0 mb-3 p-0 text-lg font-semibold text-gray-900">
                TablerIcon
            </h2>
            <p class="m-0 mb-3 text-base text-gray-600">
                Every registered icon, at the default 1.5 stroke width.
            </p>

            <div class="flex flex-wrap gap-4">
                <div    v-for="name in iconNames"
                        :key="name"
                        class="flex w-24 flex-col items-center gap-1 rounded-md border border-gray-100 p-2"
                >
                    <tabler-icon    :name="name"
                                    size-class="h-6 w-6"
                                    class="text-gray-700"
                    ></tabler-icon>
                    <span class="text-center text-xs text-gray-500">{{ name }}</span>
                </div>
            </div>
        </section>

        <!-- UserSelectInput -------------------------------------------------->
        <section class="mt-6 rounded-md border border-gray-200 bg-white p-4">
            <h2 class="m-0 mb-3 p-0 text-lg font-semibold text-gray-900">
                UserSelectInput
            </h2>

            <div class="grid grid-cols-1 gap-6 lg:grid-cols-3">
                <div>
                    <p class="m-0 mb-2 text-base font-medium text-gray-700">
                        Object shape, many users
                    </p>
                    <user-select-input  v-model="manyUsersValue"
                                        :users="manyUsers"
                                        label="Users to Send to Automatically"
                                        modal-title="Choose recipients"
                                        modal-description="They will be emailed this report each time it runs on schedule."
                                        choose-label="Choose recipients"
                                        change-label="Change recipients"
                                        empty-summary="No users selected"
                                        noun="user"
                                        plural-noun="users"
                    ></user-select-input>
                    <pre class="mt-2 overflow-x-auto rounded bg-gray-50 p-2 text-xs text-gray-700">{{ manyUsersValue }}</pre>
                </div>

                <div>
                    <p class="m-0 mb-2 text-base font-medium text-gray-700">
                        Array shape, single select
                    </p>
                    <user-select-input  v-model="singleUserValue"
                                        :users="userObjects"
                                        :multiple="false"
                                        label="Report Owner"
                                        modal-title="Choose an owner"
                                        choose-label="Choose owner"
                                        change-label="Change owner"
                                        empty-summary="No owner selected"
                                        noun="user"
                                        plural-noun="users"
                    ></user-select-input>
                    <pre class="mt-2 overflow-x-auto rounded bg-gray-50 p-2 text-xs text-gray-700">{{ singleUserValue }}</pre>
                </div>

                <div>
                    <p class="m-0 mb-2 text-base font-medium text-gray-700">
                        Empty state
                    </p>
                    <user-select-input  v-model="noUsersValue"
                                        :users="{}"
                                        label="Approvers"
                                        modal-title="Choose approvers"
                                        empty-summary="No approvers selected"
                                        empty-message="No users are available to approve this report."
                                        noun="approver"
                                        plural-noun="approvers"
                    ></user-select-input>
                    <pre class="mt-2 overflow-x-auto rounded bg-gray-50 p-2 text-xs text-gray-700">{{ noUsersValue }}</pre>
                </div>
            </div>
        </section>

        <!-- PointSelectTree -------------------------------------------------->
        <section class="mt-6 rounded-md border border-gray-200 bg-white p-4">
            <h2 class="m-0 mb-3 p-0 text-lg font-semibold text-gray-900">
                PointSelectTree
            </h2>

            <div class="grid grid-cols-1 gap-6 lg:grid-cols-2">
                <div>
                    <point-select-tree  v-model="selectedPoints"
                                        :tree="meterTree"
                    ></point-select-tree>
                </div>
                <div>
                    <p class="m-0 mb-2 text-base font-medium text-gray-700">
                        Selected point ids
                    </p>
                    <pre class="overflow-x-auto rounded bg-gray-50 p-2 text-xs text-gray-700">{{ selectedPoints }}</pre>
                </div>
            </div>
        </section>

        <!-- ReportSettingsPanel ---------------------------------------------->
        <section class="mt-6 rounded-md border border-gray-200 bg-white p-4">
            <h2 class="m-0 mb-3 p-0 text-lg font-semibold text-gray-900">
                ReportSettingsPanel
            </h2>
            <p class="m-0 mb-3 text-base text-gray-600">
                A schema exercising every registered field type, plus a
                <code>showIf</code> conditional: choose Weekly to reveal the
                weekday field.
            </p>

            <div class="grid grid-cols-1 gap-6 lg:grid-cols-2">
                <div>
                    <report-settings-panel  v-model="demoSettings"
                                            v-model:interval="demoInterval"
                                            :schema="demoSchema"
                                            :is-savable="true"
                                            timezone="America/New_York"
                    ></report-settings-panel>
                </div>

                <div>
                    <p class="m-0 mb-2 text-base font-medium text-gray-700">
                        Live settings
                    </p>
                    <pre class="overflow-x-auto rounded bg-gray-50 p-2 text-xs text-gray-700">interval: {{ demoInterval }}
{{ demoSettings }}</pre>
                </div>
            </div>
        </section>
    </div>
</template>

<script>
import PointSelectTree from "@/components/elements/PointSelectTree.vue"
import ReportPageHeader from "@/components/GroupReports/settings/ReportPageHeader.vue"
import ReportSettingsPanel from "@/components/GroupReports/settings/ReportSettingsPanel.vue"
import TablerIcon from "@/components/elements/TablerIcon.vue"
import UserSelectInput from "@/components/elements/UserSelectInput.vue"

// Enough users that the picker has to cope with a real list rather than a
// handful -- the case that motivated building it.
const buildManyUsers = () => {
    const first = ['Emerson', 'Amara', 'Isla', 'Elena', 'Rowan', 'Nadia', 'Theo', 'Priya', 'Mateo', 'Suri']
    const last = ['Chen', 'Okafor', 'Nakamura', 'Silva', 'Novak', 'Haddad', 'Lindqvist', 'Osei']
    const users = {}

    for (let i = 0; i < 100; i++) {
        users[i + 1] = `${first[i % first.length]} ${last[i % last.length]} ${Math.floor(i / first.length) + 1}`
    }

    return users
}

export default {
    name: "ReportComponentsTest",
    components: {
        PointSelectTree,
        ReportPageHeader,
        ReportSettingsPanel,
        TablerIcon,
        UserSelectInput,
    },
    data() {
        return {
            iconNames: [
                'calendar', 'users', 'user', 'pencil', 'dots', 'player', 'send',
                'history', 'archive', 'plus', 'search', 'x', 'alertTriangle',
                'help', 'eyeOff',
            ],

            manyUsers: buildManyUsers(),
            manyUsersValue: [3, 17, 42],
            userObjects: [
                {id: 1, display_name: 'Emerson Chen'},
                {id: 2, display_name: 'Amara Okafor'},
                {id: 3, display_name: 'Isla Nakamura'},
            ],
            singleUserValue: [2],
            noUsersValue: [],

            meterTree: [
                {
                    id: 1,
                    name: 'Alpha Wing',
                    zones: [
                        {
                            id: 11,
                            name: 'Pharmacy',
                            meters: [
                                {id: 101, name: 'Fridge 1'},
                                {id: 102, name: 'Fridge 2'},
                                {id: 103, name: 'Freezer A'},
                            ],
                        },
                        {
                            id: 12,
                            name: 'Lab',
                            meters: [
                                {id: 104, name: 'Incubator 1'},
                                {id: 105, name: 'Cryo Tank'},
                            ],
                        },
                    ],
                },
                {
                    id: 2,
                    name: 'Temporary Floor',
                    zones: [
                        {
                            id: 21,
                            name: 'Storage',
                            meters: [
                                {id: 201, name: 'Ambient 1'},
                                {id: 202, name: 'Ambient 2'},
                            ],
                        },
                    ],
                },
            ],
            selectedPoints: [101, 104],

            demoInterval: 'monthly',
            demoSettings: {
                includeMinMax: true,
                includeData: false,
                reportTitle: 'Monthly Compliance',
                passThreshold: 95,
                logTime1: '07:00',
                startDate: null,
                weekday: 1,
                sendTimes: ['08:00'],
            },
            demoSchema: {
                slug: 'ui-test',
                title: 'Field Type Reference',
                sections: [
                    {
                        title: 'Every Field Type',
                        description: 'One of each registered type, wired to the settings object on the right.',
                        fields: [
                            // fromContext: interval lives on the template row,
                            // not in report_settings, so it round-trips through
                            // the panel's own update:interval event.
                            {key: 'interval', type: 'select', label: 'Send interval',
                             fromContext: true,
                             options: {monthly: 'Monthly', weekly: 'Weekly', daily: 'Daily'}},
                            // Only appears for weekly, to demonstrate showIf.
                            {key: 'weekday', type: 'select', label: 'Weekday',
                             showIf: (ctx) => ctx.interval === 'weekly',
                             options: {1: 'Monday', 2: 'Tuesday', 3: 'Wednesday'}},
                            {key: 'includeMinMax', type: 'toggle', label: 'Min/Max Values',
                             description: 'Show the lowest and highest reading for each period'},
                            {key: 'includeData', type: 'toggle', label: 'Data Logs'},
                            {key: 'reportTitle', type: 'text', label: 'Report title'},
                            {key: 'passThreshold', type: 'number', label: 'Pass threshold', suffix: '%'},
                            {key: 'logTime1', type: 'time', label: 'Morning cutoff'},
                            {key: 'startDate', type: 'date', label: 'Start date'},
                            {key: 'sendTimes', type: 'repeater', label: 'Send times',
                             itemType: 'time', itemLabel: 'Send time',
                             addText: 'Add a send time',
                             emptyText: 'No send times set.',
                             default: '08:00'},
                            {key: 'derived', type: 'static', label: 'Logging Period 1',
                             value: (s) => `${s.logTime1 ?? '--'} onward`},
                            {type: 'prose', label: 'About prose fields',
                             text: 'A prose field renders explanatory copy and never reads or writes a settings key.'},
                        ],
                    },
                ],
            },
        }
    },
}
</script>
